import { FileResult } from '@imagise/entities';
import { EditMode, FilterTypes } from './enums/editor.enum';
import { normalizeRect, Rectangle } from './interfaces/shared';
import {
  EditorCommittedEdit,
  EditorDraftOperation,
  EditorPreparedCommit,
  EditorSourceImageState,
  cloneDraftOperation,
  cloneAnimationParams,
  cloneFilterArgs,
  cloneRectangle,
  isCropDraftOperation,
} from './editor-domain.models';

export function createEditorSourceImageState(
  file: FileResult | null,
): EditorSourceImageState {
  if (!file) {
    return {
      fileId: null,
      fileName: '',
      sourceBase64: null,
      mimeType: null,
      previewUrl: null,
      width: null,
      height: null,
    };
  }

  return {
    fileId: file.id || null,
    fileName: file.FileName || '',
    sourceBase64: file.SourceBase64 || null,
    mimeType: file.SourceMimeType || null,
    previewUrl: resolvePreviewUrl(file),
    width: null,
    height: null,
  };
}

/**
 * Normalizes a panel-emitted draft candidate: drops no-op edits, preserves
 * the crop rectangle across same-shape crop drafts and defensively clones
 * the accepted candidate.
 */
export function toEditorDraftOperation(
  candidate: EditorDraftOperation | null,
  previousDraft: EditorDraftOperation | null,
): EditorDraftOperation | null {
  if (!candidate) {
    return null;
  }

  switch (candidate.kind) {
    case 'crop': {
      const previousCropDraft = isCropDraftOperation(previousDraft)
        ? previousDraft
        : null;
      const preserveRect = Boolean(
        previousCropDraft &&
        previousCropDraft.params.width === candidate.params.width &&
        previousCropDraft.params.height === candidate.params.height &&
        previousCropDraft.params.units === candidate.params.units,
      );

      return {
        kind: 'crop',
        mode: EditMode.Crop,
        params: {
          width: candidate.params.width,
          height: candidate.params.height,
          units: candidate.params.units,
          rect: preserveRect && previousCropDraft
            ? cloneRectangle(previousCropDraft.params.rect)
            : null,
        },
      };
    }
    case 'resize':
      if (isNearlyEqual(candidate.params.scaleX, 1) && isNearlyEqual(candidate.params.scaleY, 1)) {
        return null;
      }
      break;
    case 'rotate': {
      const normalizedRightAngle = ((candidate.params.rightAngle % 360) + 360) % 360;
      if (
        normalizedRightAngle === 0 &&
        candidate.params.preciseAngle === 0 &&
        !candidate.params.flipX &&
        !candidate.params.flipY
      ) {
        return null;
      }
      break;
    }
    case 'filter':
      if (candidate.params.filterType === FilterTypes.None) {
        return null;
      }
      break;
    case 'watermark':
      if (!candidate.params.text.trim()) {
        return null;
      }
      break;
    case 'deskew':
    case 'animation':
    case 'merge':
      break;
  }

  return cloneDraftOperation(candidate);
}

export function withCropDraftRect(
  draft: EditorDraftOperation | null,
  rect: Rectangle | null,
): EditorDraftOperation | null {
  if (!isCropDraftOperation(draft)) {
    return draft ? cloneDraftOperation(draft) : null;
  }

  return {
    kind: 'crop',
    mode: EditMode.Crop,
    params: {
      width: draft.params.width,
      height: draft.params.height,
      units: draft.params.units,
      rect: cloneRectangle(rect),
    },
  };
}

export function toPreparedCommit(
  id: string,
  draft: EditorDraftOperation | null,
): EditorPreparedCommit | null {
  const edit = draft ? toCommittedEdit(id, draft) : null;

  return edit
    ? {
        id,
        edit,
      }
    : null;
}

export function toCommittedEdit(
  id: string,
  draft: EditorDraftOperation,
): EditorCommittedEdit | null {
  switch (draft.kind) {
    case 'crop': {
      const normalizedRect = normalizeRect(draft.params.rect);
      if (!normalizedRect) {
        return null;
      }

      return {
        id,
        kind: 'crop',
        mode: EditMode.Crop,
        params: {
          width: draft.params.width,
          height: draft.params.height,
          units: draft.params.units,
          rect: normalizedRect,
        },
      };
    }
    case 'resize':
      return {
        id,
        kind: 'resize',
        mode: EditMode.Resize,
        params: {
          scaleX: draft.params.scaleX,
          scaleY: draft.params.scaleY,
          mode: draft.params.mode,
          keepAspectRatio: draft.params.keepAspectRatio,
        },
      };
    case 'rotate':
      return {
        id,
        kind: 'rotate',
        mode: EditMode.Rotate,
        params: {
          rightAngle: draft.params.rightAngle,
          preciseAngle: draft.params.preciseAngle,
          flipX: draft.params.flipX,
          flipY: draft.params.flipY,
          cut: draft.params.cut,
        },
      };
    case 'filter':
      return {
        id,
        kind: 'filter',
        mode: EditMode.Filter,
        params: {
          filterType: draft.params.filterType,
          filterArgs: cloneFilterArgs(draft.params.filterArgs),
        },
      };
    case 'watermark':
      return {
        id,
        kind: 'watermark',
        mode: EditMode.Watermark,
        params: {
          text: draft.params.text,
          textColor: draft.params.textColor,
          fontName: draft.params.fontName,
          fontSize: draft.params.fontSize,
          rotationAngleDegrees: draft.params.rotationAngleDegrees,
        },
      };
    case 'deskew':
      return {
        id,
        kind: 'deskew',
        mode: EditMode.Deskew,
        params: {
          resizeProportionally: draft.params.resizeProportionally,
          backgroundColor: draft.params.backgroundColor,
        },
      };
    case 'animation':
      if (draft.params.frames.length < 2) {
        return null;
      }

      return {
        id,
        kind: 'animation',
        mode: EditMode.Animation,
        params: cloneAnimationParams(draft.params),
      };
    case 'merge':
      // Merge is applied through the /merge/* endpoints, never through /edit.
      return null;
  }
}

function resolvePreviewUrl(file: FileResult): string | null {
  if (file.DownloadUrl) {
    return file.DownloadUrl;
  }

  if (file.UploadFileName) {
    return file.UploadFileName;
  }

  if (file.LocalObjectUrl) {
    return file.LocalObjectUrl;
  }

  if (file.SourceBase64) {
    const mimeType = file.SourceMimeType || 'image/png';
    return `data:${mimeType};base64,${file.SourceBase64}`;
  }

  return file.id ? `/download/${encodeURIComponent(file.id)}` : null;
}

function isNearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) < 0.0001;
}
